







/**
 * Sets or returns the value of the `boundingBox` option.
 *
 * The bounding box is an area within the search results needs to limit to. If it is omitted then the
 * whole world will be taken into consideration.
 *
 * In case the area specified exceeds the world boundaries the following actions will be taken
 * depending of which side was exceeded:
 *  - Latitudes: the exceeded values will be replaced with their maximun.
 *  - Longitudes: The service will split the area into two (or more) valid bounding boxes and will
 *    execute a search request for each one and them merge the responses into a single result.
 *
 * This method is able to convert a number of popular formats into the bounding box.
 *
 * The supported formats are listed below:
 *  - `{minLon: 0, minLat: 0, maxLon: 1, maxLat: 1}` Plain object with the keys minLon, minLat, maxLon, maxLat.
 *  - `L.LatLngBounds` Leaflet's L.LatLngBounds object.
 *  - `[0, 0, 1, 1]` An array of numbers describing the bounding box following the order: minLon, minLat,
 *     maxLon, maxLat.
 *  - `ol.Extent` Open Layers extent object.
 *  - `[ol.Coordinate, ol.Coordinate]` An array of two open layers coordinate objects with the order:
 *     southWest, northEast.
 *  - `[[0, 0], [1, 1]]` A two-dimensional array with two indexes [southWest, northEast], each one with
 *     latitude and longitude values.
 *  - `"0,0,1,1"` Comma separated string with numbers in the order: minLon, minLat, maxLon, maxLat.
 *  - `[{lon: 0, lat: 0},{lon: 1, lat: 1}]` A one-dimensional array with two objects in the order: southWest,
 *     northEast and each object with a `lat` and `lon` keys.
 *  - `[{lng: 0, lat: 0},{lng: 1, lat: 1}]` A one-dimensional array with two objects in the order: southWest,
 *     northEast and each object with a `lat` and `lon` keys.
 *  - `[L.LatLng, L.LatLng]` A one-dimensional array with two L.LatLng instances in the order: southWest
 *     and northEast.
 *  - `google.maps.LatLngBounds` Google Maps bounds object.
 *  - `google.maps.LatLngBoundsLiteral` Google Maps bounds object.
 *  - `[LatLng, LatLng]` array of two google.maps.LatLng.
 *  - `[LatLngLiteral, LatLngLiteral]` array of two google.maps.LatLngLiteral.
 *
 * @method boundingBox
 * @deprecated
 * @chainable
 * @param {Object|Number[]|Object[]|String} [newValue] Bounding box area in one of the supported formats
 * @throws {TypeError} If the given argument cannot be converted into a valid value
 * @return {Object|Number[]|Object[]|String} The same service instance or the current option value if no
 *     argument was given
 */

/**
 * Allows to make search request using the TomTom's
 * [Search API - Nearby Search API](https://developer.tomtom.com/search-api/search-api-documentation-search/nearby-search).
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "NearbySearch/go:method"}}{{/crossLink}} performs the actual call.
 *
 * The call is asynchronous therefore the user have two options for receive the response:
 *  - Passing a callback function.
 *  - Use the {{#crossLink "Promise"}}{{/crossLink}} returned by the
 *    {{#crossLink "NearbySearch/go:method"}}{{/crossLink}} method to handle the response.
 *
 * @example
 * ```js
 * function callbackFn(result) {
 *   console.log(result);
 * };
 *
 * // Using a callback function
 * tomtom.nearbySearch()
 *   .key(<Your API key>) // this is optional if you defined a global key earlier
 *   .center([0,0])
 *   .radius(1000)
 *   .callback(callbackFn)
 *   .go();
 *
 * // Or using a Promise
 * tomtom.nearbySearch()
 *   .key(<Your API key>) // this is optional if you defined a global key earlier
 *   .center([0,0])
 *   .radius(1000)
 *   .go()
 *   .then(callbackFn);
 * ```
 *
 * The SDK provides a {{#crossLink "Promise"}}{{/crossLink}} polyfill for browsers (IE<9)
 * without native ECMAScript 6 support.
 *
 * @class NearbySearch
 * @extends Search
 * @uses ExtendedPostalCodesForMixin
 * @uses CenterMixin
 * @uses RadiusMixin
 * @uses CountrySetMixin
 * @uses OffsetMixin
 * @uses ViewMixin
 * @uses BestResultMixin
 * @uses TypeaheadMixin
 * @constructor
 * @module Services
 *
 * @param {Object}   [options] Options to be passed to the nearby search call,
 *     or Array of such options objects to perform batch request.
 * @param {String}   [options.key] An API Key issued from <a href="https://developer.tomtom.com">Developer Portal</a>
 * @param {"http"|"https"} [options.protocol="https"] Protocol's type
 * @param {Boolean}  [options.unwrapBbox=false] Changes the bounding box coordinates allowing longitudes that
 *  exceeds 180 degrees when is necessary. {{#crossLink "Search/unwrapBbox:method"}}More{{/crossLink}}
 * @param {Number}   [options.limit=10] Maximum number of responses that will be returned
 * @param {String} [options.view="Unified"] Geopolitical view
 * @param {String}   [options.language=null] Language to use in the response.
 *  The list with all the available codes can be found
 *  <a href="https://developer.tomtom.com/search-api/search-api-documentation/supported-languages">here</a>.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 *
 * @param {Boolean}  [options.bestResult=false] Return only one result, the best match result.
 * @param {Number[]|String|L.LatLng|Object} options.center Location where results should be biased.
 *  This parameter is required to be defined either in the options passed to the constructor or using the
 *  **center()** method.
 * @param {Number}   [options.radius=10000] The search radius in meters using the coordinates given to the **center**
 *  option as origin.
 *  It expects a value between 1 and 50000, if none is set defaults to 10000.
 * @param {Number}   [options.offset=0] Starting offset of the returned results within the full result set
 * @param {String}   [options.countrySet] List of country codes for limit the search request to those specific
 *  political limits
 * @param {Object|Number[]|Object[]|String} [options.boundingBox] DEPRECATED! The bounding box is an area within the
 * search results needs to limit to. If it is omitted then the whole world will be taken into consideration.
 * @param {String}   [options.extendedPostalCodesFor] Represent the indexes for which extended postal codes should
 *  be included in the results
 * @param {Boolean}   [options.typeahead=false] Enables of disables the option.
 */
define(['./search', '../validators'], function(search, validators) {
    'use strict';

    var SearchPrototype = search.prototype.constructor;

    function NearbySearch(options) {
        SearchPrototype.call(this, options);

        this.fields.type = {
            defaultValue: this.TYPE.nearby,
            visible: false
        };
        this.fields.query = {
            required: false,
            visible: false,
            defaultValue: ''
        };
        this.fields.center = {
            required: true
        };
        this.fields.radius = {
            required: true,
            validator: validators.integerInInterval(0, 50000),
            defaultValue: 10000
        };
        this.fields.typeahead = {
            visible: false
        };
    }

    NearbySearch.prototype = new SearchPrototype();
    NearbySearch.prototype.constructor = NearbySearch;

    return function(options) {
        return (new NearbySearch(options)).construct();
    };
});



// WEBPACK FOOTER //
// ./services/search/nearbySearch.js